// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { LoadState } from './useMlTraining';

/**
 * Named course sets: which courses a training run learns from, or an evaluation is scored on.
 *
 * A set is a name for a list of course ids with a purpose, kept in the registry so "quick" means
 * the same three courses next week as it does today. The run form expands the chosen set into
 * `--course` arguments; the tools never see the name.
 */

export type MlCourseSetPurpose = 'train' | 'evaluate';

export type MlCourseSet = {
  id: string;
  name: string;
  purpose: MlCourseSetPurpose;
  courseIds: string[];
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

/** A set as the page writes it, for create and replace alike. */
export type MlCourseSetDraft = {
  name: string;
  purpose: MlCourseSetPurpose;
  courseIds: string[];
  notes: string;
};

export type MlCourseSets = { sets: MlCourseSet[] };

/** The result of a write: the server's own reason when it refuses. */
export type CourseSetWriteResult =
  | { ok: true }
  | { ok: false; message: string | null; httpStatus: number | null };

type Row = Record<string, unknown>;

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

/** Maps the BFF's `/api/geodata/ml/course-sets` body. Exported for direct unit testing. */
export const mapCourseSets = (body: unknown): MlCourseSets => {
  const root = (body ?? {}) as Row;
  const raw = root.sets ?? root.Sets;
  return {
    sets: (Array.isArray(raw) ? (raw as Row[]) : [])
      .map((row): MlCourseSet | null => {
        const id = str(row, 'id', 'Id');
        const purpose = str(row, 'purpose', 'Purpose');
        if (!id || (purpose !== 'train' && purpose !== 'evaluate')) return null;
        const ids = row.courseIds ?? row.CourseIds;
        return {
          id,
          name: str(row, 'name', 'Name') ?? '',
          purpose,
          courseIds: Array.isArray(ids) ? ids.filter((c): c is string => typeof c === 'string') : [],
          notes: str(row, 'notes', 'Notes'),
          createdAt: str(row, 'createdAt', 'CreatedAt'),
          updatedAt: str(row, 'updatedAt', 'UpdatedAt'),
        };
      })
      .filter((s): s is MlCourseSet => s !== null),
  };
};

/** What a refused write said, out of the problem body geodata answers with. */
const reasonOf = async (response: Response): Promise<string | null> => {
  try {
    const body = (await response.json()) as Row;
    return str(body, 'detail', 'title') ?? null;
  } catch {
    return null;
  }
};

/**
 * Loads the sets, and writes them. Every write refreshes the list on success, so the set just
 * saved appears under the form that saved it.
 */
export function useMlCourseSets(
  apiBaseUrl: string,
  token: string | undefined,
  enabled: boolean
): {
  state: LoadState<MlCourseSets>;
  refresh: () => void;
  save: (id: string | null, draft: MlCourseSetDraft) => Promise<CourseSetWriteResult>;
  remove: (id: string) => Promise<CourseSetWriteResult>;
} {
  const [state, setState] = useState<LoadState<MlCourseSets>>({ status: 'loading' });
  const reloadKeyRef = useRef(0);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => {
    reloadKeyRef.current += 1;
    setReloadKey(reloadKeyRef.current);
  }, []);

  useEffect(() => {
    if (!enabled || !token) return;

    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/ml/course-sets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (response.status === 401 || response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (!response.ok) {
          setState({ status: 'failed', httpStatus: response.status });
          return;
        }
        const body = await response.json();
        if (cancelled) return;
        setState({ status: 'ready', loadedAt: Date.now(), ...mapCourseSets(body) });
      } catch {
        if (!cancelled) setState({ status: 'failed', httpStatus: null });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, token, enabled, reloadKey]);

  const write = useCallback(
    async (method: 'POST' | 'PUT' | 'DELETE', path: string, body?: MlCourseSetDraft): Promise<CourseSetWriteResult> => {
      if (!token) return { ok: false, message: null, httpStatus: 401 };
      try {
        const response = await fetch(`${apiBaseUrl}${path}`, {
          method,
          headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
          body: body ? JSON.stringify(body) : undefined,
        });
        if (!response.ok) {
          return { ok: false, message: await reasonOf(response), httpStatus: response.status };
        }
        refresh();
        return { ok: true };
      } catch {
        return { ok: false, message: null, httpStatus: null };
      }
    },
    [apiBaseUrl, token, refresh]
  );

  const save = useCallback(
    (id: string | null, draft: MlCourseSetDraft) =>
      id === null
        ? write('POST', '/api/geodata/ml/course-sets', draft)
        : write('PUT', `/api/geodata/ml/course-sets/${id}`, draft),
    [write]
  );

  const remove = useCallback((id: string) => write('DELETE', `/api/geodata/ml/course-sets/${id}`), [write]);

  return { state, refresh, save, remove };
}
